import type { CreateQBankStructureFormInput, GroupOption } from '@exam-management/common/types';
import { OrderType, useSearchQBankStructureGroupTagsLazyQuery } from '@exam-management/graphql';
import { Dialog, DialogTitle, Divider } from '@shared/components';
import { useDidUpdate } from '@shared/hooks';
import { useMemo, useState } from 'react';

import CreateQBankStructureForm from '../../components/create-qbank-structure-form/create-qbank-structure-form';
import { useQBankStructureCreationModal } from '../../providers';

interface CreateQBankStructureModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CreateQBankStructureModal(props: Readonly<CreateQBankStructureModalProps>) {
  const { open: isOpen, onClose } = props;
  const {
    submitCreateQBankStructureForm,
    setupSection,
    isCreateQBankStructureLoading,
    isGetQBankStructureLevelLoading,
  } = useQBankStructureCreationModal();

  const [groupSearchInput, setGroupSearchInput] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isGroupOpen, setIsGroupOpen] = useState(false);

  const [searchQBankStructureGroupTags, { data: groupData, loading: isGroupLoading }] =
    useSearchQBankStructureGroupTagsLazyQuery();

  const getQBankStructureGroupTags = (currentValue = '') => {
    searchQBankStructureGroupTags({
      fetchPolicy: 'network-only',
      variables: {
        search: {
          name: isSearching ? groupSearchInput : currentValue,
        },
        paging: {
          currentPage: 0,
          pageSize: 100,
        },
        order: {
          field: 'name',
          type: OrderType.Asc,
        },
      },
    });
  };

  const onCreateSubmit = async (formValues: CreateQBankStructureFormInput) => {
    const { group, ...restValue } = formValues;
    await submitCreateQBankStructureForm({
      ...restValue,
      groupTag: group ? { name: group.value as string } : null,
    });
    setupSection();
  };

  const handleClose = () => {
    onClose();
    setGroupSearchInput(null);
  };

  const handleGroupInputChange = (input: string) => {
    setGroupSearchInput(input);
    setIsSearching(true);
  };

  const handleGroupChange = (input: string) => {
    setGroupSearchInput(input);
    setIsSearching(false);
  };

  const handleGroupOpen = (currentValue: string) => {
    getQBankStructureGroupTags(currentValue);
    setIsGroupOpen(true);
  };

  const handleGroupClose = () => {
    setIsGroupOpen(false);
  };

  const handleGroupBlur = (currentValue: string) => {
    setGroupSearchInput(currentValue);
    setIsSearching(false);
  };

  const handleGroupClear = () => {
    setGroupSearchInput('');
    setIsSearching(isGroupOpen); // Fetch data when the group menu is open only
  };

  useDidUpdate(() => {
    const groupDebounceHandler = setTimeout(() => {
      if (isSearching && isGroupOpen) {
        setIsSearching(false);
        getQBankStructureGroupTags();
      }
    }, 700);
    return () => clearTimeout(groupDebounceHandler);
  }, [groupSearchInput]);

  const groupOptions = useMemo(() => {
    return !isGroupLoading && groupData && !isSearching
      ? groupData.qbsSearchQBankStructureGroupTags.tags.map<GroupOption>((tag) => {
          return { label: tag.name, value: tag.name };
        })
      : [];
  }, [isGroupLoading, groupData, isSearching]);

  const isLoading = isCreateQBankStructureLoading || isGetQBankStructureLevelLoading;

  return (
    <Dialog open={isOpen} onClose={(_, reason) => reason !== 'backdropClick' && handleClose()}>
      <DialogTitle onClose={handleClose}>Create QBank</DialogTitle>
      <Divider />
      <CreateQBankStructureForm
        onClose={handleClose}
        onCreateSubmit={onCreateSubmit}
        loading={isLoading}
        slotProps={{
          group: {
            options: groupOptions,
            isLoading: isGroupLoading || isSearching,
            onChange: handleGroupChange,
            onInputChange: handleGroupInputChange,
            onOpen: handleGroupOpen,
            onClose: handleGroupClose,
            onBlur: handleGroupBlur,
            onClear: handleGroupClear,
          },
        }}
      />
    </Dialog>
  );
}
